import ReactECharts from 'echarts-for-react';
import benchmarkData from '../data/contextInjectionBenchmark.json';

/**
 * ContextQualityChart — context tokens vs output quality across four
 * context-injection variants (Fig 1). Log-scale scatter; the variant under
 * test (rag_optimized) gets brand orange, others neutral grey — identity is
 * never color-alone, every point is labeled.
 */

type Variant = { variant: string; strategy: string; quality: number; totalTokens: number; contextTokens: number; infoRatio: number };

const LABELS: Record<string, string> = {
  none: 'No context',
  local: 'Full file',
  rag: 'RAG default',
  rag_optimized: 'RAG optimized',
};

export const ContextQualityChart = ({ height = 380 }: { height?: number }) => {
  const items = (benchmarkData as unknown as { variants: Variant[] }).variants;

  const option = {
    backgroundColor: '#171717',
    tooltip: {
      trigger: 'item',
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (p: { data: Variant }) =>
        `<b>${LABELS[p.data.variant]}</b><br/>Quality: ${p.data.quality}/24<br/>Context tokens: ${p.data.contextTokens.toLocaleString()}<br/>Info ratio: ${p.data.infoRatio}`,
    },
    grid: { left: 56, right: 32, top: 32, bottom: 56 },
    xAxis: {
      type: 'log',
      name: 'context tokens (log scale)',
      nameLocation: 'middle',
      nameGap: 32,
      nameTextStyle: { color: '#9CA3AF', fontSize: 11 },
      axisLabel: { color: '#9CA3AF', fontSize: 11 },
      splitLine: { lineStyle: { color: '#333', opacity: 0.4 } },
    },
    yAxis: {
      type: 'value',
      min: 16,
      max: 20.5,
      name: 'quality (max 24)',
      nameTextStyle: { color: '#9CA3AF', fontSize: 11 },
      axisLabel: { color: '#9CA3AF', fontSize: 11 },
      splitLine: { lineStyle: { color: '#333', opacity: 0.4 } },
    },
    series: [
      {
        type: 'scatter',
        symbolSize: (_val: number[], p: { data: Variant }) => (p.data.variant === 'rag_optimized' ? 24 : 16),
        data: items.map((i) => ({
          name: LABELS[i.variant],
          value: [i.contextTokens, i.quality],
          variant: i.variant,
          quality: i.quality,
          contextTokens: i.contextTokens,
          infoRatio: i.infoRatio,
          itemStyle: {
            color: i.variant === 'rag_optimized' ? '#FD9800' : '#9CA3AF',
            borderColor: i.variant === 'rag_optimized' ? '#C77700' : '#6B7280',
            borderWidth: i.variant === 'rag_optimized' ? 2 : 1,
          },
        })),
        label: {
          show: true,
          formatter: (p: { data: Variant }) => LABELS[p.data.variant],
          position: 'top',
          color: '#e5e5e5',
          fontSize: 11,
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 1. Context cost vs output quality, log-scale x-axis. Full-file and optimized RAG reach
        similar quality, but optimized RAG uses two orders of magnitude less context.
      </p>
    </div>
  );
};
